/**
 * 基于 stableFragments 的镜头级抽检检测：一轮解码人脸抽检 → 共识；
 * 无人脸镜头复用首轮无脸帧缓存做主体推理（无二次解码）。
 */
import { FJDecoderManager, FJDecodeMode } from '@fj/codec';
import FJUtil from '../util/FJUtil.js';
import FJVideoUtil from '../util/FJVideoUtil.js';
import FJCanvasPool from '../core/FJCanvasPool.js';
import FJRenderUtil from '../util/FJRenderUtil.js';
import FJMathUtil from '../util/FJMathUtil.js';
import FJConfig from '../config/FJConfig.js';
import FJCoreConfig from '../core/FJCoreConfig.js';
import { detectFrame } from '../lib/faceDetection/frameDetection.js';
import { detectSubjectBBox, isSubjectDetectAvailable, tryEnsureSubjectModelLoaded } from '../lib/subjectDetection/subjectDetector.js';
import { GENERATE_STATE } from '../constants/FJEnumConstant.js';
import FJDetectStore from './FJDetectStore.js';
import {
    coerceStableFragments,
    clipFragmentsToRange,
} from '../lib/faceDetection/fragmentDetect/coerceStableFragments.js';
import { buildFragmentSampleSchedule } from '../lib/faceDetection/fragmentDetect/fragmentSamplePlan.js';
import { buildFragmentConsensusSegments } from '../lib/faceDetection/fragmentDetect/fragmentFaceConsensus.js';
import { buildTimelineBlocksFromFragments } from '../lib/faceDetection/fragmentDetect/fragmentTimelineBuilder.js';
import {
    findEmptyFaceFragmentIndices,
    pickSubjectCachedSamples,
} from '../lib/faceDetection/fragmentDetect/fragmentSubjectFallback.js';

/** 解码并发槽位上限，与 FJDetectStore 一致 */
const DEFAULT_MAX_CONCURRENT_FRAMES = 20;
/** 解码帧时刻与计划抽检时刻的最大偏差（秒） */
const DEFAULT_SAMPLE_TIME_TOLERANCE_SEC = 0.06;
/** options.fps 未传入时的解码帧率兜底 */
const DECODE_FPS = 30;

/** @param {number | undefined} fps */
const resolveDecodeFps = fps => (Number.isFinite(fps) && fps > 0 ? fps : DECODE_FPS);

let _decodeBufferCanvas = null;

const isDirectDrawableFrameType = type => type === 'VIDEOFRAME' || type === 'IMAGEBITMAP';

const cloneFacesData = facesData => {
    if (!Array.isArray(facesData) || facesData.length === 0) return [];
    return facesData.map(f => ({ ...f }));
};

const resolveRowTimeSec = (frameData, seqIndex, startTime, endTime, frameStepSec) => {
    const d = frameData && typeof frameData === 'object' ? frameData : null;
    if (d) {
        const raw = d.time ?? d.timestamp ?? d.mediaTime ?? d.pts ?? d.start;
        if (typeof raw === 'number' && Number.isFinite(raw) && raw >= 0) {
            return Math.min(Math.max(startTime, raw), endTime);
        }
    }
    return Math.min(startTime + seqIndex * frameStepSec, endTime);
};

const emitProgress = (onProgress, payload) => {
    if (typeof onProgress !== 'function') return;
    try {
        onProgress(payload);
    } catch (e) {
        console.error('onProgress 回调出错:', e);
    }
};

const emitFaceTiming = (onTiming, payload) => {
    if (typeof onTiming !== 'function') return;
    try {
        onTiming(payload);
    } catch (e) {
        console.error('onTiming 回调出错:', e);
    }
};

const cleanupFrame = frame => {
    if (!frame) return;
    const inner = frame.data && typeof frame.data === 'object' ? frame.data.data : null;
    const tryClose = resource => {
        if (resource && typeof resource.close === 'function') {
            try {
                resource.close();
            } catch (e) {
                console.error('关闭帧时出错:', e);
            }
        }
    };
    tryClose(inner);
    if (frame !== inner) tryClose(frame);
};

const cleanupAllFrames = pendingFrames => {
    pendingFrames.forEach(cleanupFrame);
    pendingFrames.clear();
};

const renderDecodeBufferToCanvas = (frame, frameInfo) => {
    const { dst_width, dst_height, rotate, frameType } = frameInfo;
    const imgWidth = rotate === 90 || rotate === 270 ? dst_height : dst_width;
    const imgHeight = rotate === 90 || rotate === 270 ? dst_width : dst_height;

    const canvas = FJCanvasPool.getCanvas();
    canvas.width = dst_width;
    canvas.height = dst_height;

    if (!FJUtil.isCanvasMatchFrameType(_decodeBufferCanvas, frameType)) {
        FJCanvasPool.releaseCanvas(_decodeBufferCanvas);
        _decodeBufferCanvas = FJCanvasPool.getCanvas(FJUtil.getCtxTypeWithFrameType(frameType));
    }

    FJRenderUtil.renderDecodedFrameToCanvas(
        _decodeBufferCanvas,
        frame,
        { width: imgWidth, height: imgHeight },
        frameInfo,
    );

    const ctx = canvas.getContext('2d');
    ctx.save();
    FJMathUtil.changeOrientation(ctx, { width: imgWidth, height: imgHeight }, rotate, 1, 1);
    ctx.drawImage(_decodeBufferCanvas, 0, 0, imgWidth, imgHeight);
    ctx.restore();

    return canvas;
};

const createDetectionRaster = (source, targetWidth, targetHeight) => {
    const raster = FJCanvasPool.getCanvas();
    raster.width = targetWidth;
    raster.height = targetHeight;
    raster.getContext('2d').drawImage(source, 0, 0, targetWidth, targetHeight);
    return raster;
};

const buildSubjectDetectOpts = () => ({
    modelUrl: FJConfig.SUBJECT_MODEL_URL,
    device: FJCoreConfig.SUBJECT_DETECT_DEVICE,
    threshold: FJCoreConfig.SUBJECT_DETECT_THRESHOLD,
});

/** 人脸抽检：仅 detectFrame，不在单帧内回退主体 */
const runFaceDetectOnly = async canvas => {
    return detectFrame(canvas, {
        minConfidence: FJCoreConfig.FACE_DETECT_MIN_CONFIDENCE,
    });
};

/** 主体回退抽检：仅 detectSubjectBBox；不可用时返回空（等同纯人脸无结果） */
const runSubjectDetect = async canvas => {
    if (!FJCoreConfig.SUBJECT_DETECT_ENABLED || !isSubjectDetectAvailable()) {
        return [];
    }
    try {
        return await detectSubjectBBox(canvas, buildSubjectDetectOpts());
    } catch {
        return [];
    }
};

const buildDecodeConfig = (file, { startTime, endTime, width, height, fps }) => ({
    fileName: file?.name ?? 'video',
    width,
    height,
    start: startTime,
    end: endTime,
    speed: 1,
    fps,
    samplerate: FJUtil.getAudioSampleRate(),
    mode: FJDecodeMode.FFMPEG,
    frameType: FJVideoUtil.getDecodeVideoFrameType(),
    ignoreNoValidDuration: true,
});

/** @param {object} p */
const createFragmentDetectSession = p => ({
    ...p,
    durationSec: Math.max(0, p.endTime - p.startTime),
    frameStepSec: 1 / Math.max(1e-6, p.fps),
    activeFramesCount: 0,
    pendingFrames: new Set(),
    validFramesSizeResolve: null,
    decodeSlotWaiters: [],
    processedFrames: 0,
    decodeOrderIndex: 0,
    detectChain: Promise.resolve(),
    faceDecodeDetectT0: 0,
    scheduleCursor: 0,
    completedDetects: 0,
    /** @type {Map<number, { currentTime: number, facesData: object[] }[]>} */
    samplesByFrag: new Map(),
    /** @type {Map<number, { currentTime: number, facesData: object[] }[]>} */
    subjectSamplesByFrag: new Map(),
    /** @type {Map<number, { currentTime: number, bitmap: ImageBitmap }[]>} */
    emptyFaceBitmapCacheByFrag: new Map(),
});

const notifyValidFramesSize = session => {
    if (!session.validFramesSizeResolve) return;
    session.validFramesSizeResolve(session.maxConcurrentFrames - session.activeFramesCount);
    session.validFramesSizeResolve = null;
};

const tryResolveDecodeSlotWaiters = session => {
    if (session.decodeSlotWaiters.length > 0 && session.activeFramesCount < session.maxConcurrentFrames) {
        session.decodeSlotWaiters.shift()();
    }
};

const awaitDecodeSlot = session => {
    if (session.activeFramesCount < session.maxConcurrentFrames) return Promise.resolve();
    return new Promise(res => session.decodeSlotWaiters.push(res));
};

const getValidFramesSize = async session => {
    const available = session.maxConcurrentFrames - session.activeFramesCount;
    if (available > 0) {
        notifyValidFramesSize(session);
        return available;
    }
    return new Promise(res => {
        session.validFramesSizeResolve = res;
    });
};

const releaseFrameSlot = (session, frame, rowTime) => {
    cleanupFrame(frame);
    session.activeFramesCount--;
    session.pendingFrames.delete(frame);
    notifyValidFramesSize(session);
    tryResolveDecodeSlotWaiters(session);
    session.processedFrames += 1;
    emitProgress(session.onProgress, {
        percent: Math.min(100, (session.completedDetects / session.totalDetectEstimate) * 100),
        currentTime: rowTime,
        processedFrames: session.processedFrames,
        totalFramesEstimate: session.totalFramesEstimate,
        phase: GENERATE_STATE.PROCESSING,
    });
};

/** 当前解码时刻是否命中下一个抽检点 */
const matchSampleSlot = (session, rowTime) => {
    const { schedule, sampleTimeToleranceSec } = session;
    let i = session.scheduleCursor;
    while (i < schedule.length && rowTime > schedule[i].time + sampleTimeToleranceSec) {
        i += 1;
    }
    session.scheduleCursor = i;
    if (i >= schedule.length) {
        return null;
    }
    const slot = schedule[i];
    if (Math.abs(rowTime - slot.time) <= sampleTimeToleranceSec) {
        session.scheduleCursor = i + 1;
        return slot;
    }
    return null;
};

const releaseEmptyFaceBitmapCache = session => {
    for (const rows of session.emptyFaceBitmapCacheByFrag.values()) {
        for (const row of rows) {
            if (row.bitmap?.close) {
                try {
                    row.bitmap.close();
                } catch (e) {
                    console.error('关闭 ImageBitmap 时出错:', e);
                }
            }
        }
    }
    session.emptyFaceBitmapCacheByFrag.clear();
};

const cacheEmptyFaceBitmap = async (session, fragIndex, rowTime, rasterCanvas) => {
    try {
        const bitmap = await createImageBitmap(rasterCanvas);
        if (!session.emptyFaceBitmapCacheByFrag.has(fragIndex)) {
            session.emptyFaceBitmapCacheByFrag.set(fragIndex, []);
        }
        session.emptyFaceBitmapCacheByFrag.get(fragIndex).push({ currentTime: rowTime, bitmap });
    } catch (e) {
        console.warn('[FJFragmentDetectStore] 缓存无脸帧 ImageBitmap 失败:', e);
    }
};

const recordSample = (targetMap, fragIndex, rowTime, facesData) => {
    if (!targetMap.has(fragIndex)) {
        targetMap.set(fragIndex, []);
    }
    targetMap.get(fragIndex).push({
        currentTime: rowTime,
        facesData: cloneFacesData(facesData),
    });
};

/**
 * @param {ReturnType<typeof createFragmentDetectSession>} session
 * @param {(session: object, rasterCanvas: HTMLCanvasElement, rowTime: number, slot: { fragIndex: number }) => Promise<void>} onSample
 */
const processDecodeFrame = async (session, frame, seqIndex, onSample) => {
    let rasterCanvas = null;
    let pooledSource = null;
    let rowTime = session.startTime;

    try {
        if (!frame?.data) {
            return;
        }

        const { data } = frame;
        const { dst_width, dst_height, data: pixelData, type: frameType, rotate } = data;

        rowTime = resolveRowTimeSec(data, seqIndex, session.startTime, session.endTime, session.frameStepSec);

        const slot = matchSampleSlot(session, rowTime);
        if (!slot) {
            return;
        }

        const source = isDirectDrawableFrameType(frameType)
            ? pixelData
            : renderDecodeBufferToCanvas(pixelData, { dst_width, dst_height, rotate, frameType });

        if (!isDirectDrawableFrameType(frameType)) {
            pooledSource = source;
        }

        rasterCanvas = createDetectionRaster(source, session.width, session.height);
        if (pooledSource) {
            FJCanvasPool.releaseCanvas(pooledSource);
            pooledSource = null;
        }

        await onSample(session, rasterCanvas, rowTime, slot);
        rasterCanvas = null;
    } catch (err) {
        console.error('[FJFragmentDetectStore] 抽检帧处理出错:', err);
    } finally {
        if (pooledSource) FJCanvasPool.releaseCanvas(pooledSource);
        if (rasterCanvas) FJCanvasPool.releaseCanvas(rasterCanvas);
        releaseFrameSlot(session, frame, rowTime);
    }
};

/** 重置 schedule 相关状态，开始新一轮解码抽检 */
const resetSessionForSchedule = (session, schedule) => {
    session.schedule = schedule;
    session.scheduleCursor = 0;
    session.completedDetects = 0;
    session.totalDetectEstimate = Math.max(1, schedule.length);
    session.processedFrames = 0;
    session.decodeOrderIndex = 0;
    session.detectChain = Promise.resolve();
    session.pendingFrames = new Set();
    session.activeFramesCount = 0;
};

const handleFaceSample = async (session, rasterCanvas, rowTime, slot) => {
    const facesData = await runFaceDetectOnly(rasterCanvas);
    recordSample(session.samplesByFrag, slot.fragIndex, rowTime, facesData);
    if (facesData.length === 0) {
        await cacheEmptyFaceBitmap(session, slot.fragIndex, rowTime, rasterCanvas);
    }
    session.completedDetects += 1;
    FJCanvasPool.releaseCanvas(rasterCanvas);
};

/**
 * 按 schedule 跑一轮解码抽检。
 * @param {Blob|File} file
 * @param {ReturnType<typeof createFragmentDetectSession>} session
 * @param {{ time: number, fragIndex: number }[]} schedule
 * @param {(session: object, rasterCanvas: HTMLCanvasElement, rowTime: number, slot: { fragIndex: number }) => Promise<void>} onSample
 */
const runScheduledDecodePass = (file, session, schedule, onSample) => {
    resetSessionForSchedule(session, schedule);

    const decodeConfig = buildDecodeConfig(file, {
        startTime: session.startTime,
        endTime: session.endTime,
        width: session.width,
        height: session.height,
        fps: session.fps,
    });

    return new Promise((resolve, reject) => {
        const decodeCallback = async frame => {
            const { data, error, ended } = frame;

            if (ended) {
                await session.detectChain.catch(() => {});
                resolve();
                return;
            }
            if (error) {
                cleanupAllFrames(session.pendingFrames);
                reject(error);
                return;
            }
            if (!data) return;

            await awaitDecodeSlot(session);
            session.activeFramesCount++;
            session.pendingFrames.add(frame);
            const seqIndex = session.decodeOrderIndex++;

            session.detectChain = session.detectChain
                .then(() => processDecodeFrame(session, frame, seqIndex, onSample))
                .catch(err => console.error('[FJFragmentDetectStore] 帧处理失败:', err));
        };

        try {
            FJDecoderManager.decodeVideo(file, decodeConfig, {
                decodeCallback,
                metadataCallback: () => {},
                getValidFramesSize: () => getValidFramesSize(session),
            });
        } catch (err) {
            cleanupAllFrames(session.pendingFrames);
            reject(err);
        }
    });
};

/** @param {ReturnType<typeof createFragmentDetectSession>} session */
const computeFaceSegmentsByFrag = session => {
    /** @type {Map<number, object[]>} */
    const segmentsByFrag = new Map();
    session.fragments.forEach((_, fragIndex) => {
        const samples = session.samplesByFrag.get(fragIndex) ?? [];
        segmentsByFrag.set(fragIndex, buildFragmentConsensusSegments(samples, session.consensusOpts));
    });
    return segmentsByFrag;
};

/**
 * 对无人脸镜头复用首轮无脸帧缓存做主体推理，并写回 segments（无二次解码）。
 * @param {ReturnType<typeof createFragmentDetectSession>} session
 * @param {Map<number, object[]>} segmentsByFrag
 */
const applySubjectFallbackForEmptyFragments = async (session, segmentsByFrag) => {
    if (!FJCoreConfig.SUBJECT_DETECT_ENABLED) {
        return;
    }

    const emptyIndices = findEmptyFaceFragmentIndices(segmentsByFrag, session.fragments.length);
    if (emptyIndices.length === 0) {
        return;
    }

    const modelReady = await tryEnsureSubjectModelLoaded(buildSubjectDetectOpts());
    if (!modelReady) {
        return;
    }

    session.subjectSamplesByFrag = new Map();

    for (const fragIndex of emptyIndices) {
        const cached = session.emptyFaceBitmapCacheByFrag.get(fragIndex) ?? [];
        const toDetect = pickSubjectCachedSamples(
            session.fragments[fragIndex],
            cached,
            session.subjectFallbackSampleOpts,
            session.fps,
        );
        for (const { currentTime, bitmap } of toDetect) {
            let rasterCanvas = null;
            try {
                rasterCanvas = createDetectionRaster(bitmap, session.width, session.height);
                const facesData = await runSubjectDetect(rasterCanvas);
                recordSample(session.subjectSamplesByFrag, fragIndex, currentTime, facesData);
            } finally {
                if (rasterCanvas) FJCanvasPool.releaseCanvas(rasterCanvas);
            }
        }
    }

    for (const fragIndex of emptyIndices) {
        const samples = session.subjectSamplesByFrag.get(fragIndex) ?? [];
        const segs = buildFragmentConsensusSegments(samples, session.consensusOpts);
        if (segs.length > 0) {
            segmentsByFrag.set(fragIndex, segs);
        }
    }
};

const FJFragmentDetectStore = {
    /**
     * 按 stableFragments 镜头抽检并输出 TimelineBlock[]。
     * 无有效 fragments 时回退至 FJDetectStore。
     * @param {Object} [options]
     * @param {object} [options.subjectFallbackSampleOpts] 空镜主体回退抽检（默认 1~3 帧/镜）
     */
    async decodeAndDetect(file, options = {}) {
        const {
            startTime,
            endTime,
            width,
            height,
            maxConcurrentFrames = DEFAULT_MAX_CONCURRENT_FRAMES,
            stableFragments = [],
            fps,
            sampleTimeToleranceSec = DEFAULT_SAMPLE_TIME_TOLERANCE_SEC,
            sampleOpts,
            consensusOpts,
            subjectFallbackSampleOpts,
            onProgress,
            onTiming,
        } = options;

        const coerced = coerceStableFragments(stableFragments);
        const fragments = clipFragmentsToRange(coerced, startTime, endTime);

        if (fragments.length === 0) {
            return FJDetectStore.decodeAndDetect(file, options);
        }

        const decodeFps = resolveDecodeFps(fps);
        const faceSchedule = buildFragmentSampleSchedule(fragments, sampleOpts, decodeFps);

        await FJDetectStore.ensureModelsLoaded();
        _decodeBufferCanvas = FJCanvasPool.getCanvas();

        const session = createFragmentDetectSession({
            startTime,
            endTime,
            width,
            height,
            fps: decodeFps,
            maxConcurrentFrames,
            fragments,
            schedule: faceSchedule,
            sampleTimeToleranceSec,
            consensusOpts,
            subjectFallbackSampleOpts,
            onProgress,
            onTiming,
            totalDetectEstimate: Math.max(1, faceSchedule.length),
            totalFramesEstimate: Math.max(1, Math.ceil(Math.max(0, endTime - startTime) * decodeFps)),
        });

        emitProgress(onProgress, {
            percent: 0,
            currentTime: startTime,
            processedFrames: 0,
            totalFramesEstimate: session.totalFramesEstimate,
            phase: 'start',
        });

        session.faceDecodeDetectT0 = performance.now();

        try {
            // 一轮解码：人脸抽检（无脸帧缓存 ImageBitmap）
            await runScheduledDecodePass(file, session, faceSchedule, handleFaceSample);

            let segmentsByFrag = computeFaceSegmentsByFrag(session);

            // 共识为空的镜头：复用缓存帧做主体推理
            await applySubjectFallbackForEmptyFragments(session, segmentsByFrag);
            releaseEmptyFaceBitmapCache(session);

            const decodeDetectMs = performance.now() - session.faceDecodeDetectT0;
            emitProgress(onProgress, {
                percent: 100,
                currentTime: endTime,
                processedFrames: session.processedFrames,
                totalFramesEstimate: session.totalFramesEstimate,
                phase: 'complete',
            });
            emitFaceTiming(onTiming, {
                segmentSec: session.durationSec,
                decodeDetectMs,
                startTime,
                endTime,
            });

            return buildTimelineBlocksFromFragments(
                session.fragments,
                segmentsByFrag,
                session.startTime,
                session.endTime,
            );
        } catch (err) {
            cleanupAllFrames(session.pendingFrames);
            releaseEmptyFaceBitmapCache(session);
            throw err;
        }
    },

    ensureModelsLoaded() {
        return FJDetectStore.ensureModelsLoaded();
    },
};

export default FJFragmentDetectStore;
